import ExternalConnectionService from '@/services/ExternalConnectionService'

const state = {
  // État de chargement
  isLoading: false,
  error: null,
  
  // Connecteurs du client sélectionné
  clientConnections: [],
  clientConnectionsLoading: false,
  
  // Vue d'ensemble de tous les clients
  allClientsConnections: [],
  allClientsConnectionsLoading: false,
  
  // Types de connecteurs disponibles
  availableConnectors: [],
  availableConnectorsLoading: false,
  
  // Dialog de création/édition
  connectionDialog: false,
  editingConnection: null,
  
  // Test de connexion
  testResults: {},
  testingConnections: []
}

const mutations = {
  // Loading states
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading
  },
  SET_CLIENT_CONNECTIONS_LOADING(state, isLoading) {
    state.clientConnectionsLoading = isLoading
  },
  SET_ALL_CLIENTS_CONNECTIONS_LOADING(state, isLoading) {
    state.allClientsConnectionsLoading = isLoading
  },
  SET_AVAILABLE_CONNECTORS_LOADING(state, isLoading) {
    state.availableConnectorsLoading = isLoading
  },
  
  // Error handling
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  
  // Client connections
  SET_CLIENT_CONNECTIONS(state, connections) {
    state.clientConnections = connections
  },
  ADD_CLIENT_CONNECTION(state, connection) {
    state.clientConnections = [...state.clientConnections, connection]
  },
  UPDATE_CLIENT_CONNECTION(state, updatedConnection) {
    state.clientConnections = state.clientConnections.map(conn =>
      conn.id === updatedConnection.id ? updatedConnection : conn
    )
  },
  REMOVE_CLIENT_CONNECTION(state, connectionId) {
    state.clientConnections = state.clientConnections.filter(conn => conn.id !== connectionId)
  },
  
  // All clients connections
  SET_ALL_CLIENTS_CONNECTIONS(state, connections) {
    state.allClientsConnections = connections
  },
  
  // Available connectors
  SET_AVAILABLE_CONNECTORS(state, connectors) {
    state.availableConnectors = connectors
  },
  
  // Dialog management
  SET_CONNECTION_DIALOG(state, isOpen) {
    state.connectionDialog = isOpen
  },
  SET_EDITING_CONNECTION(state, connection) {
    state.editingConnection = connection
  },
  
  // Test results
  SET_TEST_RESULT(state, { connectionId, result }) {
    state.testResults = {
      ...state.testResults,
      [connectionId]: result
    }
  },
  ADD_TESTING_CONNECTION(state, connectionId) {
    if (!state.testingConnections.includes(connectionId)) {
      state.testingConnections = [...state.testingConnections, connectionId]
    }
  },
  REMOVE_TESTING_CONNECTION(state, connectionId) {
    state.testingConnections = state.testingConnections.filter(id => id !== connectionId)
  }
}

const actions = {
  /**
   * Charge les connecteurs d'un client spécifique
   */
  loadClientConnections({ commit }, clientSerial) {
    commit('SET_CLIENT_CONNECTIONS_LOADING', true)
    commit('CLEAR_ERROR')
    
    return ExternalConnectionService.getClientConnections(clientSerial)
      .then(connections => {
        commit('SET_CLIENT_CONNECTIONS', connections)
        return connections
      })
      .catch(error => {
        console.error('Erreur lors du chargement des connecteurs du client:', error)
        commit('SET_ERROR', error.message)
        throw error
      })
      .finally(() => {
        commit('SET_CLIENT_CONNECTIONS_LOADING', false)
      })
  },

  /**
   * Charge la vue d'ensemble de tous les clients
   */
  loadAllClientsConnections({ commit }) {
    commit('SET_ALL_CLIENTS_CONNECTIONS_LOADING', true)
    commit('CLEAR_ERROR')
    
    return ExternalConnectionService.getAllClientsConnections()
      .then(connections => {
        commit('SET_ALL_CLIENTS_CONNECTIONS', connections)
        return connections
      })
      .catch(error => {
        console.error('Erreur lors du chargement de la vue d\'ensemble:', error)
        commit('SET_ERROR', error.message)
        throw error
      })
      .finally(() => {
        commit('SET_ALL_CLIENTS_CONNECTIONS_LOADING', false)
      })
  },

  /**
   * Charge les types de connecteurs disponibles
   */
  loadAvailableConnectors({ commit }) {
    commit('SET_AVAILABLE_CONNECTORS_LOADING', true)
    commit('CLEAR_ERROR')
    
    return ExternalConnectionService.getAvailableConnectors()
      .then(connectors => {
        commit('SET_AVAILABLE_CONNECTORS', connectors)
        return connectors
      })
      .catch(error => {
        console.error('Erreur lors du chargement des connecteurs disponibles:', error)
        commit('SET_ERROR', error.message)
        throw error
      })
      .finally(() => {
        commit('SET_AVAILABLE_CONNECTORS_LOADING', false)
      })
  },

  /**
   * Crée un nouveau connecteur
   */
  createConnection({ commit }, { clientSerial, connectionData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    return ExternalConnectionService.createConnection(clientSerial, connectionData)
      .then(connection => {
        commit('ADD_CLIENT_CONNECTION', connection)
        return connection
      })
      .catch(error => {
        console.error('Erreur lors de la création du connecteur:', error)
        commit('SET_ERROR', error.message)
        throw error
      })
      .finally(() => {
        commit('SET_LOADING', false)
      })
  },

  /**
   * Met à jour un connecteur existant
   */
  updateConnection({ commit }, { connectionId, connectionData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    return ExternalConnectionService.updateConnection(connectionId, connectionData)
      .then(connection => {
        commit('UPDATE_CLIENT_CONNECTION', connection)
        return connection
      })
      .catch(error => {
        console.error('Erreur lors de la mise à jour du connecteur:', error)
        commit('SET_ERROR', error.message)
        throw error
      })
      .finally(() => {
        commit('SET_LOADING', false)
      })
  },

  /**
   * Supprime un connecteur
   */
  deleteConnection({ commit }, connectionId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    return ExternalConnectionService.deleteConnection(connectionId)
      .then(() => {
        commit('REMOVE_CLIENT_CONNECTION', connectionId)
      })
      .catch(error => {
        console.error('Erreur lors de la suppression du connecteur:', error)
        commit('SET_ERROR', error.message)
        throw error
      })
      .finally(() => {
        commit('SET_LOADING', false)
      })
  },

  /**
   * Active/désactive un connecteur
   */
  toggleConnection({ commit }, { connectionId, enabled }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    return ExternalConnectionService.toggleConnection(connectionId, enabled)
      .then(connection => {
        commit('UPDATE_CLIENT_CONNECTION', connection)
        return connection
      })
      .catch(error => {
        console.error('Erreur lors du toggle du connecteur:', error)
        commit('SET_ERROR', error.message)
        throw error
      })
      .finally(() => {
        commit('SET_LOADING', false)
      })
  },
  
  /**
   * Ouvre le dialog de création/édition
   */
  openConnectionDialog({ commit }, connection = null) {
    commit('SET_EDITING_CONNECTION', connection)
    commit('SET_CONNECTION_DIALOG', true)
  },

  /**
   * Ferme le dialog de création/édition
   */
  closeConnectionDialog({ commit }) {
    commit('SET_CONNECTION_DIALOG', false)
    commit('SET_EDITING_CONNECTION', null)
  },

  /**
   * Nettoie l'état du store
   */
  clearState({ commit }) {
    commit('SET_CLIENT_CONNECTIONS', [])
    commit('SET_ALL_CLIENTS_CONNECTIONS', [])
    commit('CLEAR_ERROR')
  }
}

const getters = {
  // Loading states
  isAnyLoading: state => 
    state.isLoading || 
    state.clientConnectionsLoading || 
    state.allClientsConnectionsLoading || 
    state.availableConnectorsLoading,
  
  // Connecteurs filtrés par type
  getConnectionsByType: state => type => 
    state.clientConnections.filter(conn => conn.type === type),
  
  // Connecteurs actifs
  activeConnections: state => 
    state.clientConnections.filter(conn => conn.enabled),
  
  // Connecteurs inactifs
  inactiveConnections: state => 
    state.clientConnections.filter(conn => !conn.enabled),
  
  // Statut de test pour un connecteur
  getTestResult: state => connectionId => 
    state.testResults[connectionId],
  
  // Vérifier si un connecteur est en cours de test
  isConnectionTesting: state => connectionId => 
    state.testingConnections.includes(connectionId),
  
  // Obtenir les connecteurs disponibles non utilisés
  getAvailableConnectorsForClient: state => {
    const usedTypes = state.clientConnections.map(conn => conn.type)
    return state.availableConnectors.filter(connector => !usedTypes.includes(connector.type))
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}